import React, { useMemo, useState } from "react";
import { Box, Button, Paper, SvgIcon, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import * as d3 from "d3";
import { useLocation, useNavigate } from "react-router-dom";
import LayoutMapping, { useLayoutMappingMetrics } from "../components/LayoutMapping";
import WorkspaceSidebar from "../components/WorkspaceSidebar";
import { ARAGON_COLORS, ARAGON_FONTS } from "../theme/AragonTheme";
import { formatVndCompact, getCaseById, readWatchlist, toggleWatchlist } from "./buyerWorkspaceData";
import { getSellerMarketStatus } from "./advisorWorkspaceData";

function BookmarkIcon(props) {
  return (
    <SvgIcon {...props} viewBox="0 0 24 24">
      <path d="M7 4h10v16l-5-3.2L7 20V4Z" fill="currentColor" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" />
    </SvgIcon>
  );
}
function ArrowIcon(props) {
  return (
    <SvgIcon {...props} viewBox="0 0 24 24">
      <path d="M5 12h13m-5-5 5 5-5 5" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
    </SvgIcon>
  );
}

function useWatchlistMetrics() {
  const shared = useLayoutMappingMetrics();
  return useMemo(() => {
    const width = shared.viewport.width;
    const height = shared.viewport.height;
    return {
      ...shared,
      contentWidth: Math.round(d3.scaleLinear().domain([1024, 1920]).range([700, 980]).clamp(true)(width)),
      contentTop: Math.round(d3.scaleLinear().domain([620, 1080]).range([20, 30]).clamp(true)(height)),
      rowPadY: Math.round(d3.scaleLinear().domain([768, 1920]).range([15, 19]).clamp(true)(width)),
    };
  }, [shared]);
}

function formatAddedAt(value) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return new Intl.DateTimeFormat("en-GB", { day: "numeric", month: "short", year: "numeric" }).format(date);
}

export default function BuyerWatchlist() {
  const metrics = useWatchlistMetrics();
  const navigate = useNavigate();
  const location = useLocation();
  const [entries, setEntries] = useState(() => readWatchlist().sort((a, b) => new Date(b.addedAt) - new Date(a.addedAt)));
  const rows = entries.map((entry) => ({ entry, deal: getCaseById(entry.id) })).filter(({ deal }) => Boolean(deal) && getSellerMarketStatus(deal.id) === "approved");
  const remove = (caseId) => {
    const result = toggleWatchlist(caseId);
    setEntries(result.entries.sort((a, b) => new Date(b.addedAt) - new Date(a.addedAt)));
  };
  const openDeal = (caseId) => navigate(`/buyer/deals/${caseId}`, { state: { ...location.state, origin: "/buyer/watchlist" } });
  return (
    <LayoutMapping metrics={metrics} sidebar={<WorkspaceSidebar metrics={metrics} workspace="buyer" />}>
      <Box>
        <Typography sx={{ fontFamily: ARAGON_FONTS.secondary, fontSize: `${metrics.eyebrowSize}px`, fontWeight: 800, letterSpacing: "0.15em", textTransform: "uppercase", color: ARAGON_COLORS.burgundy }}>Buyer workspace</Typography>
        <Typography component="h1" sx={{ mt: "5px", fontFamily: ARAGON_FONTS.primary, fontSize: `${metrics.titleSize}px`, lineHeight: 1.04, color: ARAGON_COLORS.navy }}>Watchlist</Typography>
        <Typography sx={{ mt: "6px", fontFamily: ARAGON_FONTS.secondary, fontSize: `${metrics.bodySize}px`, color: ARAGON_COLORS.steel }}>Cases you have saved for later review, ordered by the most recently added.</Typography>

        <Paper elevation={0} sx={{ mt: `${metrics.cardGap + 14}px`, border: `1px solid ${alpha(ARAGON_COLORS.navy, 0.10)}`, borderRadius: `${metrics.cardRadius}px`, bgcolor: ARAGON_COLORS.sellerSidebar, overflow: "hidden" }}>
          <Box sx={{ px: `${metrics.cardPadding}px`, py: `${metrics.cardPadding - 3}px`, display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: "14px", borderBottom: rows.length ? `1px solid ${alpha(ARAGON_COLORS.navy, 0.08)}` : "none" }}>
            <Box>
              <Typography sx={{ fontFamily: ARAGON_FONTS.primary, fontSize: `${metrics.sectionTitleSize}px`, color: ARAGON_COLORS.navy }}>Saved cases</Typography>
              <Typography sx={{ mt: "3px", fontFamily: ARAGON_FONTS.secondary, fontSize: `${Math.max(8, metrics.bodySize - 3)}px`, color: ARAGON_COLORS.steel }}>{rows.length} {rows.length === 1 ? "case" : "cases"}</Typography>
            </Box>
            <Typography sx={{ fontFamily: ARAGON_FONTS.secondary, fontSize: `${Math.max(8, metrics.bodySize - 3)}px`, color: ARAGON_COLORS.steel }}>Newest first</Typography>
          </Box>

          {rows.length === 0 ? (
            <Box sx={{ minHeight: "250px", px: `${metrics.cardPadding}px`, display: "grid", placeItems: "center", textAlign: "center" }}>
              <Box>
                <Box sx={{ mx: "auto", width: "44px", height: "44px", borderRadius: "50%", bgcolor: alpha(ARAGON_COLORS.navy, 0.045), display: "grid", placeItems: "center", color: ARAGON_COLORS.navy }}><BookmarkIcon sx={{ fontSize: "19px" }} /></Box>
                <Typography sx={{ mt: "12px", fontFamily: ARAGON_FONTS.primary, fontSize: `${metrics.sectionTitleSize}px`, color: ARAGON_COLORS.navy }}>No saved cases yet</Typography>
                <Typography sx={{ mt: "5px", fontFamily: ARAGON_FONTS.secondary, fontSize: `${Math.max(9, metrics.bodySize - 2)}px`, color: ARAGON_COLORS.steel }}>Cases you add to your watchlist will appear here.</Typography>
              </Box>
            </Box>
          ) : rows.map(({ entry, deal }, index) => (
            <Box
              key={deal.id}
              role="button"
              tabIndex={0}
              onClick={() => openDeal(deal.id)}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") openDeal(deal.id);
              }}
              sx={{ px: `${metrics.cardPadding}px`, py: `${metrics.rowPadY}px`, display: "grid", gridTemplateColumns: metrics.compact ? "1fr auto" : "minmax(0, 1.55fr) minmax(100px, .55fr) minmax(100px, .55fr) auto", gap: "16px", alignItems: "center", borderTop: index === 0 ? "none" : `1px solid ${alpha(ARAGON_COLORS.navy, 0.07)}`, cursor: "pointer", transition: "background-color 140ms ease", "&:hover": { bgcolor: alpha(ARAGON_COLORS.navy, 0.026) }, "&:focus-visible": { outline: `1px solid ${alpha(ARAGON_COLORS.navy, 0.20)}`, outlineOffset: "-1px" } }}
            >
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontFamily: ARAGON_FONTS.secondary, fontSize: `${Math.max(8, metrics.eyebrowSize - 1)}px`, letterSpacing: "0.10em", color: ARAGON_COLORS.steel }}>{deal.id}</Typography>
                <Typography sx={{ mt: "4px", fontFamily: ARAGON_FONTS.secondary, fontSize: `${Math.max(10, metrics.bodySize - 1)}px`, fontWeight: 700, color: ARAGON_COLORS.navy, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{deal.title}</Typography>
                <Typography sx={{ mt: "3px", fontFamily: ARAGON_FONTS.secondary, fontSize: `${Math.max(8, metrics.bodySize - 3)}px`, color: ARAGON_COLORS.steel }}>{deal.location} · Added {formatAddedAt(entry.addedAt)}</Typography>
              </Box>
              {!metrics.compact && (
                <Box>
                  <Typography sx={{ fontFamily: ARAGON_FONTS.secondary, fontSize: `${Math.max(7, metrics.eyebrowSize - 1)}px`, letterSpacing: "0.10em", textTransform: "uppercase", color: ARAGON_COLORS.steel }}>Revenue</Typography>
                  <Typography sx={{ mt: "5px", fontFamily: 'Georgia, "Times New Roman", serif', fontSize: `${Math.max(11, metrics.bodySize)}px`, color: ARAGON_COLORS.navy }}>{formatVndCompact(deal.revenue)}</Typography>
                </Box>
              )}
              {!metrics.compact && (
                <Box>
                  <Typography sx={{ fontFamily: ARAGON_FONTS.secondary, fontSize: `${Math.max(7, metrics.eyebrowSize - 1)}px`, letterSpacing: "0.10em", textTransform: "uppercase", color: ARAGON_COLORS.steel }}>EBITDA</Typography>
                  <Typography sx={{ mt: "5px", fontFamily: 'Georgia, "Times New Roman", serif', fontSize: `${Math.max(11, metrics.bodySize)}px`, color: ARAGON_COLORS.navy }}>{formatVndCompact(deal.ebitda)}</Typography>
                </Box>
              )}
              <Box sx={{ display: "flex", alignItems: "center", gap: "7px" }}>
                <Button
                  aria-label={`Remove ${deal.id} from watchlist`}
                  onClick={(event) => {
                    event.stopPropagation();
                    remove(deal.id);
                  }}
                  sx={{ minWidth: "32px", width: "32px", height: "32px", p: 0, color: ARAGON_COLORS.navy, borderRadius: "50%", "&:hover": { bgcolor: alpha(ARAGON_COLORS.navy, 0.055) } }}
                >
                  <BookmarkIcon sx={{ fontSize: "16px" }} />
                </Button>
                <ArrowIcon sx={{ fontSize: "17px", color: ARAGON_COLORS.steel }} />
              </Box>
            </Box>
          ))}
        </Paper>
      </Box>
    </LayoutMapping>
  );
}
